// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaButtonClickEvent } from "@stoneage/App/Utilities/DataLayer";
import Button, { ButtonPresentationProps, ButtonVariants } from "@stoneage/MobiusOverrides/Button/Button";
import { xScreen } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    tagLine = "tagLine",
    title = "title",
    copy = "copy",
    buttonText = "buttonText",
    buttonStyle = "buttonStyle",
    buttonId = "buttonId",
    url = "url",
    openInNewTab = "openInNewTab",
    buttonText1 = "buttonText1",
    buttonStyle1 = "buttonStyle1",
    buttonId1 = "buttonId1",
    url1 = "url1",
    openInNewTab1 = "openInNewTab1",
    colorTheme = "colorTheme",
    gaClickEvent = "gaClickEvent",
    gaBtnClickEventName = "gaBtnClickEventName",
    gaBtn1ClickEventName = "gaBtn1ClickEventName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.tagLine]: string;
        [fields.title]: string;
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.buttonStyle]: ButtonVariants;
        [fields.buttonId]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.buttonText1]: string;
        [fields.buttonStyle1]: ButtonVariants;
        [fields.buttonId1]: string;
        [fields.url1]: LinkFieldValue;
        [fields.openInNewTab1]: boolean;
        [fields.colorTheme]: "light" | "dark";
        [fields.gaClickEvent]: boolean;
        [fields.gaBtnClickEventName]: string;
        [fields.gaBtn1ClickEventName]: string;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    container?: InjectableCss;
    tagLineContainer?: InjectableCss;
    tagLine?: TypographyProps;
    title?: TypographyProps;
    copy?: TypographyProps;
    button?: ButtonPresentationProps;
    buttonContainer?: InjectableCss;
}

export const infoCardStyles: InfoCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    tagLineContainer: {
        css: css`
            background-size: contain;
            background-repeat: no-repeat;
            padding: 7px 17px;
            padding-top: 4px;
            font-size: 10px;
            font-weight: bold;
            letter-spacing: 1px;
            margin-bottom: 20px;
        `,
    },
    title: {
        css: css`
            margin-bottom: 16px;
        `,
    },
    copy: {
        lineHeight: "26px",
        css: css`
            margin-bottom: 4px;
        `,
    },
    button: {
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: fit-content;
            }
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            gap: 24px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                gap: 20px;
            }
        `,
    },
};

const styles = infoCardStyles;

const InfoCard = ({
    id,
    fields: {
        tagLine,
        title,
        copy,
        buttonText,
        buttonStyle,
        buttonId,
        url,
        openInNewTab,
        buttonText1,
        buttonStyle1,
        buttonId1,
        url1,
        openInNewTab1,
        colorTheme,
        gaClickEvent,
        gaBtnClickEventName,
        gaBtn1ClickEventName,
    },
}: Props) => {
    const linkUrl = useGetLink(url);
    const linkUrl1 = useGetLink(url1);

    const getTagLine = () => {
        if (!tagLine) {
            return null;
        }

        const textColor = colorTheme === "light" ? "common.white" : "common.black";
        const backgroundImage =
            colorTheme === "light"
                ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='212' height='26' viewBox='0 0 212 26'%3E%3Cpath d='M211.674818 1.5 L204.37047 25.5 L1.5 25.5 L1.5 24 L2.5 24 L2.5 24.5 L203.629 24.5 L210.324 2.5 L209.284 2.5 L209.588 1.5 L211.674818 1.5 Z M0 23 L0 0 L209 0 L202 23 L0 23 Z'/%3E%3C/svg%3E")`
                : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='167' height='26' viewBox='0 0 167 26'%3E%3Cpath fill='%23FFF' d='M166.674818 1.5 L159.37047 25.5 L1.5 25.5 L1.5 24 L2.5 24 L2.5 24.5 L158.629 24.5 L165.324 2.5 L164.284 2.5 L164.588 1.5 L166.674818 1.5 Z M0 23 L0 0 L164 0 L157 23 L0 23 Z'/%3E%3C/svg%3E")`;
        return (
            <StyledWrapper
                css={css`
                    background-image: ${backgroundImage};
                    ${styles.tagLineContainer?.css}
                `}
            >
                <Typography color={textColor}>{tagLine}</Typography>
            </StyledWrapper>
        );
    };

    const getTitleColor = () => (colorTheme === "light" ? "common.black" : "common.white");

    const getCopyColor = () => (colorTheme === "light" ? "text.main" : "common.ui5");

    const { customerNumber, userName, impersonation } = useAppSelector(state => {
        const session = getSession(state);
        return {
            customerNumber: getBillToCustomerNumber(state),
            userName: session.userName,
            impersonation: trackAsImpersonation(state),
        };
    });

    return (
        <StyledWrapper className="info-card" {...styles.container}>
            {getTagLine()}
            <Typography {...styles.title} color={getTitleColor()}>
                {parse(title, parserOptions)}
            </Typography>
            <Typography {...styles.copy} color={getCopyColor()}>
                {parse(copy, parserOptions)}
            </Typography>
            <StyledWrapper {...styles.buttonContainer}>
                <Button
                    variant={buttonStyle}
                    target={openInNewTab ? "_blank" : ""}
                    href={linkUrl?.url}
                    {...styles.button}
                    {...(buttonId && { id: `${buttonId}_0_${id}` })}
                    {...(gaClickEvent &&
                        gaBtnClickEventName && {
                            onClick: () => {
                                trackGaButtonClickEvent(
                                    gaBtnClickEventName,
                                    buttonText,
                                    impersonation,
                                    customerNumber,
                                    undefined,
                                    userName,
                                );
                            },
                        })}
                >
                    {buttonText}
                </Button>
                {buttonText1 && (
                    <Button
                        variant={buttonStyle1}
                        target={openInNewTab1 ? "_blank" : ""}
                        href={linkUrl1?.url}
                        {...styles.button}
                        {...(buttonId1 && { id: `${buttonId1}_1_${id}` })}
                        {...(gaClickEvent &&
                            gaBtn1ClickEventName && {
                                onClick: () => {
                                    trackGaButtonClickEvent(
                                        gaBtn1ClickEventName,
                                        buttonText1,
                                        impersonation,
                                        customerNumber,
                                        undefined,
                                        userName,
                                    );
                                },
                            })}
                    >
                        {buttonText1}
                    </Button>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        displayName: "Info Card",
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.tagLine,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.title,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.buttonText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.buttonStyle,
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
            },
            {
                name: fields.buttonId,
                displayName: "Button Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.buttonText1,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.buttonStyle1,
                editorTemplate: "DropDownField",
                defaultValue: "secondary",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                ],
            },
            {
                name: fields.buttonId1,
                displayName: "Button Id1",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.url1,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.openInNewTab1,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
            {
                name: fields.gaClickEvent,
                displayName: "Track GA4 Click Event",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.gaBtnClickEventName,
                displayName: "Btn Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.gaClickEvent,
            },
            {
                name: fields.gaBtn1ClickEventName,
                displayName: "Btn1 Click Event Name",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.gaClickEvent,
            },
        ],
    },
};

export default widgetModule;
